import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, Check, Calendar, Store, Tag, Percent, ClipboardList } from "lucide-react";
import { Pro013CampaignData } from "../create-pro013-form";
import { formatDateToBR } from "../../../utils/date-utils";
import { formatPromotionText } from "../../../utils/format-promotion-text";

interface StepFiveReviewProps {
  data: Pro013CampaignData;
  prevStep: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  mode?: 'create' | 'edit';
}

const discountTypeLabels: Record<number, string> = {
  0: "Tipo 0 - Desconto Integral (Menor Valor)",
  1: "Tipo 1 - Percentual (Menor Valor)",
  2: "Tipo 2 - Valor Fixo (Menor Valor)",
  3: "Tipo 3 - Desconto Integral",
  4: "Tipo 4 - Percentual",
  5: "Tipo 5 - Valor Fixo",
  7: "Tipo 7 - Brinde",
  8: "Tipo 8 - Brinde (Mesmo Item)",
};

const paymentMethodLabels: Record<number, string> = {
  0: "Todas",
  1: "Dinheiro",
  2: "Cartão de Crédito",
  3: "Cartão de Débito",
  4: "PIX",
};

export default function StepFiveReview({ data, prevStep, onSubmit, isLoading, mode = 'create' }: StepFiveReviewProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Passo 5: Revisão e Confirmação</CardTitle>
        <CardDescription>
          Revise todas as informações antes de {mode === 'create' ? 'criar' : 'atualizar'} a campanha PRO013
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Informações Gerais */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Tag className="h-5 w-5 text-primary" />
            Informações Gerais
          </div>
          <div className="bg-secondary/20 rounded-lg p-4 space-y-3 border">
            <div>
              <p className="text-sm text-muted-foreground">Descrição</p>
              <p className="font-medium">{data.description || "-"}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Data de Início
                </p>
                <p className="font-medium">
                  {formatDateToBR(data.startDate)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  Data de Término
                </p>
                <p className="font-medium">
                  {formatDateToBR(data.endDate)}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Store className="h-3 w-3" />
                Lojas Participantes
              </p>
              <p className="font-medium">
                {data.allStores
                  ? "Todas as lojas (código 999)"
                  : `${data.stores.length} loja(s) específica(s): ${data.stores.join(", ")}`}
              </p>
            </div>
          </div>
        </div>

        {/* Configuração de Desconto */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Percent className="h-5 w-5 text-primary" />
            Configuração de Desconto
          </div>
          <div className="bg-secondary/20 rounded-lg p-4 space-y-3 border">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Quantidade "Leve"</p>
                <p className="font-bold text-lg">{data.takeQuantity}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Quantidade "Pague"</p>
                <p className="font-bold text-lg">{data.payQuantity}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tipo de Desconto</p>
              <p className="font-medium">
                {discountTypeLabels[data.discountType] || `Tipo ${data.discountType}`}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Valor/Percentual</p>
                <p className="font-medium">
                  {data.discountValue}
                  {[1, 4].includes(data.discountType) ? "%" : ""}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Forma de Pagamento</p>
                <p className="font-medium">
                  {paymentMethodLabels[data.paymentMethod] || "Todas"}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Aplicar em toda loja?</p>
              <p className="font-medium">
                {data.allStoreFlag === 1 ? "Sim" : "Não"}
              </p>
            </div>
          </div>
        </div>

        {/* Departamentos e Grupos */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <Check className="h-5 w-5 text-primary" />
            Departamentos e Grupos
          </div>
          <div className="bg-secondary/20 rounded-lg p-4 space-y-3 border">
            <div>
              <p className="text-sm text-muted-foreground">Departamentos</p>
              <p className="font-medium">
                {data.departments.length > 0
                  ? data.departments.join(", ")
                  : "Nenhum departamento selecionado"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Grupos</p>
              <p className="font-medium">
                {data.groups.length > 0
                  ? data.groups.join(", ")
                  : "Nenhum grupo selecionado"}
              </p>
            </div>
          </div>
        </div>

        {/* Resumo da Regra - STORY-NEW-039: Texto formatado baseado no tipo de desconto */}
        <div className="bg-secondary/20 border rounded-lg p-4">
          <p className="text-sm font-semibold mb-2 flex items-center gap-1">
            <ClipboardList className="h-4 w-4" /> Resumo da Promoção:
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>{formatPromotionText(data.takeQuantity, data.payQuantity, data.discountType)}</strong>
            {" "}nos departamentos/grupos selecionados.
            {data.discountValue > 0 && (
              <span> Desconto de <strong>{data.discountValue}</strong> {[1, 4].includes(data.discountType) ? "%" : "R$"}.</span>
            )}
          </p>
          <p className="text-sm mt-2 text-muted-foreground">
            <strong>Multi-loja:</strong>{" "}
            {data.allStores
              ? "Será criado 1 registro com código 999 (todas as lojas)"
              : `Serão criados ${data.stores.length} registros, um para cada loja selecionada`}
          </p>
        </div>

        {/* Alerta Final */}
        <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-300 dark:border-orange-900 rounded-lg p-4">
          <p className="text-sm text-orange-800 dark:text-orange-300">
            <strong>Atenção:</strong> Após confirmar, a campanha será {mode === 'create' ? 'criada' : 'atualizada'} no banco de dados.
            Certifique-se de que todas as informações estão corretas antes de prosseguir.
          </p>
        </div>

        {/* Botões */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={prevStep} disabled={isLoading}>
            <ChevronLeft className="mr-2 h-4 w-4" /> Voltar
          </Button>
          <Button onClick={onSubmit} disabled={isLoading} size="lg">
            {isLoading
              ? (mode === 'create' ? "Criando..." : "Salvando...")
              : (mode === 'create' ? "Criar Campanha" : "Salvar Alterações")
            }
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
